import React, { useState } from 'react'
import { useCookies } from 'react-cookie'
import classNames from 'classnames'

import ZapierPromoPrimary from '../../../assets/images/promo-zapier-primary.png'
import ZapierPromoGhost from '../../../assets/images/promo-zapier-ghost.png'

import { DELTA_TRIAL_FEATURES } from '../../../views/Plan/DeltaTrial/DeltaTrialFeatures'
import { Upsell } from '../../../views/Plan/DeltaTrial/Upsell/Upsell'

import PrizeBadge from '../../ScopedBlock/PrizeBadge.svg?url'
import { useZocksQuota } from '../../ScopedBlock/hooks'
import { Button } from '../../Button/Button'
import { Modal } from '../../Modal/Modal'

import './ZapierPromoBanner.scss'

interface ZapierPromoBannerProps {
  orientation?: 'vertical' | 'horizontal'
  variant?: 'primary' | 'ghost'
}

const _COOKIE_NAME = 'hideZapierPromoBanner'
const _COOKIE_MAX_AGE = 60 * 60 * 24 * 365

export const ZapierPromoBanner: React.FC<ZapierPromoBannerProps> = ({ orientation = 'vertical', variant = 'primary' }) => {
  const [cookies, setCookie] = useCookies()
  const { hasQuota } = useZocksQuota()

  const oauth2Enabled = hasQuota('integration.oauth2Enabled.value')

  const [showModal, setShowModal] = useState<boolean>(false)

  const handleHideBanner = () => setCookie(_COOKIE_NAME, true, { path: '/', maxAge: _COOKIE_MAX_AGE })

  const handleShowModal = () => {
    setShowModal(true)
  }

  const handleHideModal = () => {
    setShowModal(false)
  }

  if (cookies[_COOKIE_NAME]) {
    return null
  }

  const PromoImage = variant === 'primary' ? ZapierPromoPrimary : ZapierPromoGhost

  return (
    <>
      {showModal ? (
        <Modal
          size='xl'
          className='d-flex'
          bodyClassName='p-0'
          show={showModal}
          onHide={handleHideModal}
          body={
            <div className='d-flex align-items-center justify-content-center position-relative zapier-promo-modal'>
              <Upsell feature={DELTA_TRIAL_FEATURES['zapierIntegration']} />
              <i className='bi bi-x-lg position-absolute zapier-promo-modal-hide-button cursor-pointer' onClick={handleHideModal} />
            </div>
          }
          centered={true}
        />
      ) : null}
      <div
        className={classNames('d-flex gap-3 rounded-2 position-relative zapier-promo-container', {
          'zocks-bg-color-primary-light': variant === 'primary',
          'zocks-bg-color-gray-100 border zocks-border-color-gray-300': variant === 'ghost',
          'align-items-center zocks-py-1.25 zocks-ps-1.25 zocks-pe-2.25': orientation === 'horizontal',
          'zocks-p-1.25': orientation === 'vertical',
        })}
      >
        <img src={PromoImage} alt='Upgrade' />
        <div
          className={classNames('d-flex flex-1 gap-2', {
            'align-items-center justify-content-between': orientation === 'horizontal',
            'flex-column': orientation === 'vertical',
          })}
        >
          <div className='d-flex flex-column gap-2'>
            <span className='paragraph-medium zocks-text-gray-900'>
              Connect Zapier to automatically trigger workflows when Client Signals are detected in your Account.
            </span>
            {oauth2Enabled ? (
              <a
                className='zocks-text-primary paragraph-small text-decoration-underline'
                href='https://help.zocks.io/en/articles/13428173-zapier-integration'
                target='_blank'
                rel='noopener noreferrer'
              >
                Learn how to connect Zapier
              </a>
            ) : null}
          </div>
          {!oauth2Enabled ? (
            <Button
              variant='primary'
              className='p-2 button-medium zapier-promo-button d-flex align-items-center gap-2 align-self-start flex-shrink-0'
              onClick={handleShowModal}
            >
              Upgrade
              <img alt='Upgrade' src={PrizeBadge} />
            </Button>
          ) : null}
        </div>
        <i className='ps-6 bi bi-x position-absolute cursor-pointer' onClick={handleHideBanner} />
      </div>
    </>
  )
}
